/**
 * Unified video URL normalisation.
 *
 * Consolidates `getEmbedUrl()` from embedded-media.tsx and
 * `resolveVideoUrl()` from PageSectionGenerator into one utility.
 */

/**
 * Converts a YouTube/Vimeo URL (or bare ID) into an embeddable iframe URL.
 * Returns the original URL if it doesn't match a known pattern.
 */
export const getVideoEmbedUrl = (input: string): string | null => {
  if (!input) return null;

  const trimmed = input.trim();

  // Already an embed URL — pass through
  if (trimmed.includes('youtube.com/embed/') || trimmed.includes('player.vimeo.com/video/')) {
    return trimmed;
  }

  // youtube.com/watch?v=ID
  if (trimmed.includes('youtube.com/watch')) {
    try {
      const id = new URL(trimmed).searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    } catch {
      return null;
    }
  }

  // youtu.be/ID
  if (trimmed.includes('youtu.be/')) {
    const id = trimmed.split('youtu.be/')[1]?.split(/[?#]/)[0];
    return id ? `https://www.youtube.com/embed/${id}` : null;
  }

  // vimeo.com/ID or vimeo.com/ID/PRIVACY_HASH
  if (trimmed.includes('vimeo.com/')) {
    const path = trimmed.split('vimeo.com/')[1]?.split(/[?#]/)[0];
    if (!path) return null;
    const segments = path.split('/').filter(Boolean);
    const id = segments[0];
    const hash = segments[1];
    if (!id || !/^\d+$/.test(id)) return null;
    return hash
      ? `https://player.vimeo.com/video/${id}?h=${hash}`
      : `https://player.vimeo.com/video/${id}`;
  }

  // Bare YouTube ID (11 chars, alphanumeric + - _)
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
    return `https://www.youtube.com/embed/${trimmed}`;
  }

  // Direct video URL (e.g. MP4)
  return trimmed;
};

/**
 * Resolve a video source value which may be a plain string or a Contentful
 * `text` entry reference (`{ fields: { text: "..." } }`).
 */
export const resolveVideoSource = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'fields' in value) {
    const entry = value as { fields?: { text?: string } };
    if (typeof entry.fields?.text === 'string') return entry.fields.text;
  }
  return undefined;
};
